import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BlogDetailPage() {
  const { blogId } = useParams();
  const { t, language } = useLanguage();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${blogId}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Navbar />
        <h2 className="text-2xl text-gray-400">Blog post not found</h2>
        <Link to="/blog" className="btn-primary mt-4">
          {t('blog.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <article className="pt-32 pb-20" data-testid="blog-detail-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog" data-testid="back-to-blog-button" className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 mb-8">
            <ArrowLeft size={20} />
            <span>{t('blog.back')}</span>
          </Link>

          <div className="image-overlay rounded-2xl mb-8 h-96">
            <img
              src={blog.image_url}
              alt={language === 'tr' ? blog.title_tr : blog.title_en}
              className="w-full h-full object-cover rounded-2xl"
            />
          </div>

          <div className="flex items-center space-x-4 text-sm text-gray-400 mb-6">
            <div className="flex items-center space-x-1">
              <User size={16} />
              <span>{blog.author}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar size={16} />
              <span>{formatDate(blog.created_at)}</span>
            </div>
          </div>

          <h1 className="font-emblema text-3xl sm:text-4xl lg:text-5xl text-gradient mb-8" data-testid="blog-title">
            {language === 'tr' ? blog.title_tr : blog.title_en}
          </h1>

          <div className="prose prose-invert max-w-none" data-testid="blog-content">
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed whitespace-pre-wrap">
              {language === 'tr' ? blog.content_tr : blog.content_en}
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

export default BlogDetailPage;